import { FetchQueryOptions, queryOptions } from '@tanstack/react-query';

import { AgentName } from '@/constant/agents';
import {
  MessageRequest,
  MessageResponse,
  sessionApi,
  SessionHistoryResponse,
} from '@/services/sessions';
import { ChatType } from '@/store/chats';
import {
  GET_CONNIE_SESSION_HISTORY_QUERY_KEY,
  GET_DOCUMENT_SESSION_HISTORY_QUERY_KEY,
  GET_QUOTE_SESSION_HISTORY_QUERY_KEY,
  SESSIONS_QUERY_OPTIONS,
} from '@/store/sessions';

export type AgentChatType = Exclude<ChatType, 'contact'>;
type SupportedAgentName = Extract<AgentName, 'Connie' | 'Tia' | 'Salma'>;

type SendPayloadParams = {
  message: string;
  entityId?: string;
  contactId?: string;
  sessionId?: string;
};

export const CHAT_CONFIG: Record<
  AgentChatType,
  {
    agentName: SupportedAgentName;
    buildSendPayload: (params: SendPayloadParams) => MessageRequest;
    sendMessage: (
      payload: MessageRequest
    ) => Promise<MessageResponse | undefined>;
    historyQueryKey: string;
    historyFetcher: (
      sessionId: string
    ) => ReturnType<typeof sessionApi.getSessionHistory>;
    historyQueryOptions: (
      sessionId: string
    ) => FetchQueryOptions<SessionHistoryResponse | undefined>;
  }
> = {
  job: {
    agentName: AgentName.CONNIE,
    buildSendPayload: ({ message, entityId, contactId, sessionId }) => ({
      message,
      jobId: entityId,
      contactId,
      ...(sessionId && { session_id: sessionId }),
    }),
    sendMessage: sessionApi.sendMessage,
    historyQueryKey: GET_CONNIE_SESSION_HISTORY_QUERY_KEY,
    historyFetcher: sessionApi.getConnieSessionHistory,
    historyQueryOptions: (sessionId: string) =>
      queryOptions({
        queryKey: [
          GET_CONNIE_SESSION_HISTORY_QUERY_KEY,
          { sessionId },
        ] as const,
        queryFn: () => sessionApi.getConnieSessionHistory(sessionId),
        ...SESSIONS_QUERY_OPTIONS,
      }) as FetchQueryOptions<SessionHistoryResponse | undefined>,
  },
  connie: {
    agentName: AgentName.CONNIE,
    buildSendPayload: ({ message, sessionId }) => ({
      message,
      ...(sessionId && { session_id: sessionId }),
    }),
    sendMessage: sessionApi.sendConnieMessage,
    historyQueryKey: GET_CONNIE_SESSION_HISTORY_QUERY_KEY,
    historyFetcher: sessionApi.getConnieSessionHistory,
    historyQueryOptions: (sessionId: string) =>
      queryOptions({
        queryKey: [
          GET_CONNIE_SESSION_HISTORY_QUERY_KEY,
          { sessionId },
        ] as const,
        queryFn: () => sessionApi.getConnieSessionHistory(sessionId),
        ...SESSIONS_QUERY_OPTIONS,
      }) as FetchQueryOptions<SessionHistoryResponse | undefined>,
  },
  document: {
    agentName: AgentName.TIA,
    buildSendPayload: ({ message, entityId, sessionId }) => ({
      message,
      doc_id: entityId,
      ...(sessionId && { session_id: sessionId }),
    }),
    sendMessage: sessionApi.sendDocumentMessage,
    historyQueryKey: GET_DOCUMENT_SESSION_HISTORY_QUERY_KEY,
    historyFetcher: sessionApi.getDocumentSessionHistory,
    historyQueryOptions: (sessionId: string) =>
      queryOptions({
        queryKey: [
          GET_DOCUMENT_SESSION_HISTORY_QUERY_KEY,
          { sessionId },
        ] as const,
        queryFn: () => sessionApi.getDocumentSessionHistory(sessionId),
        ...SESSIONS_QUERY_OPTIONS,
      }) as FetchQueryOptions<SessionHistoryResponse | undefined>,
  },
  quote: {
    agentName: AgentName.SALMA,
    buildSendPayload: ({ message, entityId, sessionId }) => ({
      message,
      doc_id: entityId,
      ...(sessionId && { session_id: sessionId }),
    }),
    sendMessage: sessionApi.sendQuoteMessage,
    historyQueryKey: GET_QUOTE_SESSION_HISTORY_QUERY_KEY,
    historyFetcher: sessionApi.getDocumentSessionHistory,
    historyQueryOptions: (sessionId: string) =>
      queryOptions({
        queryKey: [GET_QUOTE_SESSION_HISTORY_QUERY_KEY, { sessionId }] as const,
        queryFn: () => sessionApi.getDocumentSessionHistory(sessionId),
        ...SESSIONS_QUERY_OPTIONS,
      }) as FetchQueryOptions<SessionHistoryResponse | undefined>,
  },
};
